import { ApiResponse, isApiResponse, StrapiError } from '@dh/types';
import { getLogger } from '../logger/logger';

enum ContentTypes {
    JSON = 'application/json',
    FORMDATA = 'multbla/formadata'
}

type RequestOptions = {
    url: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    accessToken: string;
    body?: FormData | string;
    contentType?: ContentTypes;
    noCache?: boolean;
};

const logger = getLogger('requestToCMS');

const noApiJwt = <T, E>(opts?: { message?: string }): ApiResponse<T> => {
    return {
        data: null,
        error: {
            name: 'RequestFailedError',
            status: 401 /* Unauthorized */,
            message: opts?.message ?? 'JWT Token is missing'
        } satisfies StrapiError<E>
    };
};

const requestToCMS = async <T, E = any>({
    url,
    method,
    body,
    accessToken,
    contentType = ContentTypes.JSON
}: RequestOptions): Promise<ApiResponse<T, E>> => {
    logger.debug(`${method} ${url}\n  ${body}`);
    // first we want to fail fast when a token is missing
    if (!accessToken) {
        logger.error(`Request to '${method} ${url}' failed:JWT Token is missing`);
        return noApiJwt<T, E>();
    }
    const headers: Record<string, string> = {
        Authorization: `Bearer ${accessToken}`
    };

    if (contentType !== ContentTypes.FORMDATA && (method === 'POST' || method === 'PUT')) {
        headers['Content-Type'] = contentType;
    }

    const response = await fetch(url, {
        method,
        headers,
        cache: 'no-cache',
        body
    });

    if (!response.ok) {
        let error: any;
        try {
            error = await response.json();
        } catch (e) {
            error = e;
        }

        logger.error(
            error,
            `Request to ${response.url} failed with status code ${response.status}: ${response.statusText}; ${error?.message}`
        );

        return {
            data: null,
            error: {
                name: 'RequestFailedError',
                status: response.status,
                message: `Request to ${response.url} failed with status code ${response.status}: ${response.statusText}`,
                details: error
            }
        };
    }
    logger.debug(`  Response: ${response.status} ${response.statusText}`);

    // deletions do not return content in strapi v5
    if (method === 'DELETE' && response.status === 204)
        return { data: null, meta: undefined, error: undefined };

    return response
        .json()
        .then((parsedBody) => {
            /*
            We have to check, if the parsed response body matches the default strapi response wrapper. This has to be
            done, as Strapi Plugins might not play by the rules and return different data formats, most of the time just
            the plain data you requested. But even plugin endpoints might return the default response wrapper which is the
            case if you receive an authentication error for example (which is handled probably by a strapi middleware).
             
            If the type matches the StrapiApiResponse we just return it, otherwise we will wrap the body as data inside
            the StrapiApiResponse, to have an aligned reliable API.  
             */
            if (isApiResponse<T, E>(parsedBody)) {
                return parsedBody;
            }
            return { data: parsedBody, meta: undefined, error: undefined };
        })
        .catch((e) => {
            logger.error(e, `Failed to parse response body from ${response.url}`);
            return {
                data: null,
                meta: undefined,
                error: {
                    name: 'RequestFailedError',
                    status: response.status,
                    message: `Parsing response body from ${response.url} failed: ${e}`
                }
            };
        });
};

export { requestToCMS, ContentTypes };
